<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import SalesProgressBlocks from './SalesProgressBlocks.vue';

const props = defineProps({ transaction: { type: Object, required: true } });
const { t } = useI18n();

const currency = computed(() =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: props.transaction.currency || 'BRL',
  }).format(Number(props.transaction.contracted_total || 0))
);

const address = computed(
  () =>
    [
      props.transaction.address?.endereco,
      props.transaction.address?.bairro,
      props.transaction.address?.cidade,
      props.transaction.address?.uf,
    ]
      .filter(Boolean)
      .join(' · ') || t('SALES.COMMON.ADDRESS_UNAVAILABLE')
);
</script>

<template>
  <div class="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(250px,0.45fr)]">
    <section class="rounded-lais border border-lais-border-soft bg-white p-4">
      <span
        class="text-xs font-bold uppercase tracking-wide text-lais-text-muted"
        >{{ t('SALES.DETAIL.PROPERTY') }}</span
      >
      <h3 class="mt-1 font-redHatDisplay text-xl font-semibold text-lais-text">
        {{ transaction.title || transaction.reference }}
      </h3>
      <p class="mt-1 text-sm text-lais-text-body">{{ address }}</p>
      <p v-if="transaction.reference" class="mt-2 text-xs text-lais-text-muted">
        {{ transaction.reference }}
      </p>
      <SalesProgressBlocks
        class="mt-5 max-w-lg"
        :progress="transaction.progress"
        :blocks="transaction.block_progress"
      />
    </section>
    <section class="rounded-lais border border-lais-border-soft bg-white p-4">
      <dl class="grid gap-4 text-sm">
        <div>
          <dt
            class="text-xs font-bold uppercase tracking-wide text-lais-text-muted"
          >
            {{ t('SALES.DETAIL.STAGE') }}
          </dt>
          <dd class="mt-1 font-semibold text-lais-text">
            {{ transaction.sales_stage_label }}
          </dd>
        </div>
        <div>
          <dt
            class="text-xs font-bold uppercase tracking-wide text-lais-text-muted"
          >
            {{ t('SALES.DETAIL.CONTRACTED_TOTAL') }}
          </dt>
          <dd class="mt-1 font-semibold text-lais-text">{{ currency }}</dd>
        </div>
        <div>
          <dt
            class="text-xs font-bold uppercase tracking-wide text-lais-text-muted"
          >
            {{ t('SALES.DETAIL.MANAGER') }}
          </dt>
          <dd class="mt-1 text-lais-text-body">
            {{ transaction.manager?.name || t('SALES.COMMON.UNASSIGNED') }}
          </dd>
        </div>
        <div>
          <dt
            class="text-xs font-bold uppercase tracking-wide text-lais-text-muted"
          >
            {{ t('SALES.DETAIL.RESPONSIBLE') }}
          </dt>
          <dd class="mt-1 text-lais-text-body">
            {{
              transaction.post_sale?.responsible?.name ||
              t('SALES.COMMON.UNASSIGNED')
            }}
          </dd>
        </div>
      </dl>
    </section>
  </div>

  <section
    class="mt-4 rounded-lais border border-lais-border-soft bg-white p-4"
  >
    <h3 class="font-redHatDisplay text-lg font-semibold text-lais-text">
      {{ t('SALES.DETAIL.POST_SALE') }}
    </h3>
    <p v-if="transaction.post_sale" class="mt-2 text-sm text-lais-text-body">
      {{ transaction.post_sale.status_label }}
      <span
        v-if="transaction.post_sale.status_due_at"
        class="text-lais-text-muted"
      >
        ·
        {{
          new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(
            new Date(transaction.post_sale.status_due_at)
          )
        }}</span
      >
    </p>
    <p v-else class="mt-2 text-sm text-lais-text-muted">
      {{ t('SALES.COMMON.UNASSIGNED') }}
    </p>
  </section>
</template>
